import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Search, MapPin, Calendar, Users, Home, Loader2, ChevronDown, Map as MapIcon } from "lucide-react";
import { externalSupabase } from "@/lib/externalSupabase";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import ListingsPropertyCard, { type ListingsProperty } from "@/components/ListingsPropertyCard";
import EmailCaptureModal from "@/components/EmailCaptureModal";
import ListingsMap from "@/components/ListingsMap";
import { GOOGLE_MAPS_API_KEY } from "@/lib/googleMaps";

const PROPERTY_TYPES = ["All Types", "House", "Villa", "Villa Resort", "Cabin", "Hotel", "Bungalow", "Serviced Apartment", "Bed and Breakfast", "Treehouse"];

const ListingsPage = () => {
  const [location, setLocation] = useState("");
  const [checkIn, setCheckIn] = useState<Date | undefined>();
  const [checkOut, setCheckOut] = useState<Date | undefined>();
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [propertyType, setPropertyType] = useState("All Types");
  const [properties, setProperties] = useState<ListingsProperty[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [emailUnlocked, setEmailUnlocked] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<ListingsProperty | null>(null);
  const [hoveredPropertyId, setHoveredPropertyId] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(true);

  const fetchProperties = async (searchLocation?: string, limit = 50) => {
    setLoading(true);
    setSearched(true);
    setProperties([]);

    try {
      let query = externalSupabase
        .from("properties")
        .select("id, masked_title, original_name, image_url, city, country, bedrooms, owner_website, is_verified, property_type, latitude, longitude, street_address")
        .order("created_at", { ascending: false });

      if (searchLocation?.trim()) {
        query = query.or(`city.ilike.%${searchLocation.trim()}%,country.ilike.%${searchLocation.trim()}%`);
      }

      if (propertyType && propertyType !== "All Types") {
        query = query.eq("property_type", propertyType);
      }

      const { data, error } = await query.limit(limit);

      if (error) {
        console.error("Search error:", error);
      } else {
        const results = (data as ListingsProperty[]) || [];
        setProperties(results);
        // Reverse geocode properties missing street_address
        reverseGeocodeProperties(results);
      }
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const reverseGeocodeProperties = async (props: ListingsProperty[]) => {
    const needsGeocode = props.filter(
      (p) => !p.street_address && p.latitude != null && p.longitude != null
    );
    if (needsGeocode.length === 0) return;

    try {
      const geocoder = new (window.google.maps.Geocoder as any)();
      const updates = await Promise.allSettled(
        needsGeocode.map((p) =>
          new Promise<{ id: string; address: string }>((resolve, reject) => {
            geocoder.geocode(
              { location: { lat: p.latitude!, lng: p.longitude! } },
              (results, status) => {
                if (status === "OK" && results?.[0]) {
                  resolve({ id: p.id, address: results[0].formatted_address });
                } else {
                  reject(status);
                }
              }
            );
          })
        )
      );

      const addressMap = new Map<string, string>();
      updates.forEach((result) => {
        if (result.status === "fulfilled") {
          addressMap.set(result.value.id, result.value.address);
        }
      });

      if (addressMap.size > 0) {
        setProperties((prev) =>
          prev.map((p) =>
            addressMap.has(p.id) ? { ...p, street_address: addressMap.get(p.id)! } : p
          )
        );
        // Persist to DB
        addressMap.forEach((address, id) => {
          externalSupabase
            .from("properties")
            .update({ street_address: address })
            .eq("id", id)
            .then(({ error }) => {
              if (error) console.error("Failed to save address for", id, error);
            });
        });
      }
    } catch (err) {
      console.error("Reverse geocoding failed:", err);
    }
  };

  // Load initial properties on mount
  useEffect(() => {
    fetchProperties(undefined, 12);
  }, []);

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    fetchProperties(location);
  };

  const handleViewDetails = (property: ListingsProperty) => {
    if (emailUnlocked) {
      if (property.owner_website) {
        window.open(property.owner_website, "_blank", "noopener,noreferrer");
      }
      return;
    }
    setSelectedProperty(property);
    setEmailModalOpen(true);
  };

  const handleEmailSuccess = () => {
    setEmailUnlocked(true);
    setEmailModalOpen(false);
    setSelectedProperty(null);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 pt-24 flex flex-col">
        {/* Search bar */}
        <div className="sticky top-16 z-30 bg-background/95 backdrop-blur-sm border-b py-4">
          <form onSubmit={handleSearch} className="container mx-auto px-4 max-w-6xl">
            <div className="flex flex-wrap items-center gap-2 bg-card border rounded-2xl p-2 shadow-sm">
              {/* Location */}
              <div className="flex items-center gap-2 flex-1 min-w-[180px] px-3 py-2">
                <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Where to?"
                  className="bg-transparent text-sm w-full focus:outline-none placeholder:text-muted-foreground"
                />
              </div>

              <div className="w-px h-8 bg-border hidden md:block" />

              {/* Check-in */}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-xl transition-colors min-w-[130px]">
                    <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className={checkIn ? "text-foreground" : "text-muted-foreground"}>
                      {checkIn ? format(checkIn, "MMM d") : "Check in"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent mode="single" selected={checkIn} onSelect={setCheckIn} disabled={(date) => date < new Date()} />
                </PopoverContent>
              </Popover>

              <div className="w-px h-8 bg-border hidden md:block" />

              {/* Check-out */}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-xl transition-colors min-w-[130px]">
                    <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className={checkOut ? "text-foreground" : "text-muted-foreground"}>
                      {checkOut ? format(checkOut, "MMM d") : "Check out"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent mode="single" selected={checkOut} onSelect={setCheckOut} disabled={(date) => date < new Date() || (checkIn ? date <= checkIn : false)} />
                </PopoverContent>
              </Popover>

              <div className="w-px h-8 bg-border hidden md:block" />

              {/* Guests */}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-xl transition-colors">
                    <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-foreground">{adults + children} guest{adults + children !== 1 ? "s" : ""}</span>
                    <ChevronDown className="w-3 h-3 text-muted-foreground" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56" align="start">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Adults</span>
                      <div className="flex items-center gap-2">
                        <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => setAdults(Math.max(1, adults - 1))} disabled={adults <= 1}>-</Button>
                        <span className="text-sm w-6 text-center">{adults}</span>
                        <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => setAdults(adults + 1)}>+</Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Children</span>
                      <div className="flex items-center gap-2">
                        <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => setChildren(Math.max(0, children - 1))} disabled={children <= 0}>-</Button>
                        <span className="text-sm w-6 text-center">{children}</span>
                        <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => setChildren(children + 1)}>+</Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              <div className="w-px h-8 bg-border hidden md:block" />

              {/* Property Type */}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-xl transition-colors">
                    <Home className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-foreground">{propertyType}</span>
                    <ChevronDown className="w-3 h-3 text-muted-foreground" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-40" align="start">
                  <div className="space-y-1">
                    {PROPERTY_TYPES.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setPropertyType(t)}
                        className={`w-full text-left text-sm px-3 py-1.5 rounded-lg transition-colors ${propertyType === t ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Map toggle */}
              <Button
                type="button"
                variant={showMap ? "default" : "outline"}
                size="icon"
                className="rounded-xl"
                onClick={() => setShowMap(!showMap)}
                title={showMap ? "Hide map" : "Show map"}
              >
                <MapIcon className="w-4 h-4" />
              </Button>

              {/* Search button */}
              <Button type="submit" disabled={loading} className="bg-gradient-hero rounded-xl px-5 shadow-savings">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
          </form>
        </div>

        <div className="flex-1 container mx-auto px-4 max-w-[1400px] mt-6 pb-16">
          {/* Results count */}
          {searched && !loading && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-muted-foreground mb-4">
              {properties.length > 0
                ? `${properties.length} ${properties.length === 1 ? "home" : "homes"} found`
                : "No properties found for this location."}
            </motion.p>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
              <p className="text-sm text-muted-foreground">Searching properties...</p>
            </div>
          )}

          {/* Split view: grid + map */}
          {searched && !loading && properties.length > 0 && (
            <div className={`flex gap-6 ${showMap ? "" : ""}`}>
              {/* Property grid */}
              <div className={`${showMap ? "w-1/2 xl:w-3/5 max-h-[calc(100vh-12rem)] overflow-y-auto pr-2" : "w-full"} transition-all duration-300`}>
                <div className={`grid ${showMap ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"} gap-5`}>
                  {properties.map((prop, i) => (
                    <div
                      key={prop.id}
                      id={`property-${prop.id}`}
                      onMouseEnter={() => setHoveredPropertyId(prop.id)}
                      onMouseLeave={() => setHoveredPropertyId(null)}
                    >
                      <ListingsPropertyCard
                        property={prop}
                        index={i}
                        onViewDetails={handleViewDetails}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Map */}
              {showMap && (
                <div className="w-1/2 xl:w-2/5 hidden md:block">
                  <div className="sticky top-40 h-[calc(100vh-12rem)] rounded-2xl overflow-hidden border shadow-sm">
                    <ListingsMap
                      properties={properties}
                      highlightedId={hoveredPropertyId}
                      onMarkerClick={(prop) => {
                        const el = document.getElementById(`property-${prop.id}`);
                        el?.scrollIntoView({ behavior: "smooth", block: "center" });
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {searched && !loading && properties.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Search className="w-7 h-7 text-accent-foreground" />
              </div>
              <h2 className="font-display text-xl font-bold mb-2">No Listings Found</h2>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                No verified listings yet. Start a search to add one!
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* Email Capture Modal */}
      <EmailCaptureModal
        open={emailModalOpen}
        onClose={() => { setEmailModalOpen(false); setSelectedProperty(null); }}
        onSuccess={handleEmailSuccess}
        listingUrl={selectedProperty?.id || ""}
      />
    </div>
  );
};

export default ListingsPage;
